<template>
  <div class="auth-page animate-fadeIn">
    <div class="login-header">
      <a href="https://thunderstock.nl">
        <img
          class="animate-float"
          :src="thunderstockAotLogo"
          alt="Thunderstock"
        >
      </a>
    </div>
    <a
      class="info-link"
      href="https://thunderstock.ai"
    >
      Info
    </a>
    <div class="auth-main">
      <div class="top-section" />
      <v-alert
        v-if="errorMessage"
        density="compact"
        variant="outlined"
        type="error"
        closable
        class="alert"
        @click:close="errorMessage = null"
      >
        {{ errorMessage }}
      </v-alert>
      <v-alert
        v-else-if="successMessage"
        density="compact"
        variant="outlined"
        type="success"
        closable
        class="alert"
        @click:close="successMessage = null"
      >
        {{ successMessage }}
      </v-alert>
      <div class="mid-section">
        <v-card class="login-card">
          <v-row
            no-gutters
            class="ma-0 justify-space-between align-center card-head"
          >
            <v-col>
              <h3 class="auth-title ma-auto animate-pulse-hover">
                {{ title }}
              </h3>
            </v-col>
            <v-col
              v-if="superuser"
              class="d-flex align-center justify-end"
            >
              <label class="mr-2 dev-mode">Dev mode</label>
              <v-switch
                :model-value="devMode"
                hide-details
                class="mt-0"
                @update:model-value="handleDevModeChanged"
              />
            </v-col>
          </v-row>
          <v-defaults-provider :defaults="{ VTextField: { hideDetails: false } }">
            <router-view
              v-model:success-message="successMessage"
              v-model:error-message="errorMessage"
            />
          </v-defaults-provider>
        </v-card>
      </div>
      <div class="bottom-section" />
      <footer class="auth-footer">
        {{ aotClock }}
      </footer>
    </div>
  </div>
</template>
<script>
import { mapState } from 'pinia'
import { useGeneralStore } from '@/store/pinia/generalStore'
import { getRefreshToken } from '@/auth/tokenService'
import thunderstockAotLogo from '@/assets/img/thunderstock-aot-logo.png'

export default {
  name: 'AuthLayout',
  data () {
    return {
      thunderstockAotLogo,
      superuser: localStorage.getItem('devMode') === 'true',
      errorMessage: null,
      successMessage: null,
      aotClock: '',
      aotTimer: null
    }
  },
  computed: {
    ...mapState(useGeneralStore, ['devMode']),
    title () {
      return this.$route.meta['title'] || 'Login'
    }
  },
  watch: {
    '$route' () {
      this.errorMessage = null
      this.successMessage = null
    },
    errorMessage (val) {
      if (val) {
        this.successMessage = null
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    successMessage (val) {
      if (val) {
        this.errorMessage = null
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    }
  },
  async created () {
    // Already-authenticated user landing on the login page: try to resume the
    // session and forward into the app. On failure tokenService clears tokens.
    if (getRefreshToken()) {
      const result = await this.$auth.refreshTokens()
      if (result.ok) {
        this.$router.push({ path: '/stock/home' })
      }
    }
  },
  mounted () {
    this.updateAotClock()
    this.aotTimer = setInterval(this.updateAotClock, 10000)
  },
  beforeUnmount () {
    clearInterval(this.aotTimer)
  },
  methods: {
    handleDevModeChanged () {
      useGeneralStore().setDevMode(!this.devMode)
    },
    // "Ahead Of Time": show the visitor's local time plus five minutes, with
    // their own GMT offset (e.g. "AOT 20:18 GMT+4").
    updateAotClock () {
      const ahead = new Date(Date.now() + 5 * 60 * 1000)
      const hh = String(ahead.getHours()).padStart(2, '0')
      const mm = String(ahead.getMinutes()).padStart(2, '0')
      const offsetMin = -new Date().getTimezoneOffset()
      const sign = offsetMin >= 0 ? '+' : '-'
      const oh = Math.floor(Math.abs(offsetMin) / 60)
      const om = Math.abs(offsetMin) % 60
      const gmt = `GMT${sign}${oh}${om ? ':' + String(om).padStart(2, '0') : ''}`
      this.aotClock = `AOT ${hh}:${mm} ${gmt}`
    },
  },
}
</script>
<style lang="scss">
.auth-page {
  zoom: calc(1 / var(--zoom));
  .auth-main{
    // Fixed full-viewport layer so the image fills the whole screen exactly,
    // with no document overflow (which would let the page scroll into white).
    position: fixed;
    inset: 0;
    // Frame the image 200px higher than centre, but never let its top edge drop
    // below the viewport top (which would expose a black bar behind the logo/Info
    // on short-overflow viewports) — clamp the shift to 0. Fade into black
    // towards the bottom where the footer clock sits.
    background-color: #000000;
    background-image:
      linear-gradient(to bottom, rgba(0, 0, 0, 0) 45%, rgba(0, 0, 0, 0.75) 80%, #000000 100%),
      url("../../assets/img/login-bg-delorean.jpg");
    background-repeat: no-repeat;
    background-size: 100% 100%, cover;
    background-position:
      center,
      center min(calc(50% + 200px), 0px);
    height: 100%;
    .card-head {
      margin-bottom: 14px!important;
      max-height: 55px;

      // The label inherits an 8px bottom margin (global `label` rule) which
      // pushes it up in the centered row; balance it with a matching top margin
      // so the "Dev mode" text centers with the switch and the card title.
      .dev-mode {
        margin-top: 8px;
      }
    }
    .auth-title {
      color: #FF840E;
      font-size: 20px;
    }
    .error-msg {
      color: var(--danger);
      text-align: center;
    }

    label {
      display: inline-block;
      font-size: 15px;
      color: #011413;
      font-weight: 500;
      margin-bottom: 8px;
    }
    button {
      height: 40px!important;
      font-size: 15px!important;
      width: 100%;
      &.action {
        color: #FF840E !important;
      }
      &.v-btn {
        font-weight: 500;
      }
    }
    a {
      font-size: 15px;
      text-decoration: none!important;
      &.text-primary {
        color: rgb(var(--v-theme-primary)) !important;
      }
    }
    .login-card {
      width: 450px;
      min-height: 490px;
      padding: 24px 50px 50px;
      display: flex;
      flex-direction: column;
      border-radius: 10px !important;
      background-color: #E8E7E3 !important;

      .divider-row {
        width: 350px;
        text-align: center;
        margin-inline: auto;

        // V4 renders a horizontal v-divider full-width; make the two dividers
        // flex so they flank the "OR" (line — OR — line) as on dev.
        .v-divider {
          flex: 1 1 0;
        }
      }
    }

    .top-section {
      height: 15%;
      width: 100vw;
    }

    .bottom-section {
      width: 100vw;
      height: 15%;
    }
    .mid-section {
      height: 80% !important;
      width: 100vw;
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 1;
      position: relative;
    }
    .v-alert {
      position: absolute !important;
      top: 15px!important;
      left: 25%;
      right: 25%;
      margin-left: auto;
      margin-right: auto;
      max-width: 300px;
      z-index: 100;
      background-color: rgba(255, 255, 255, 0.9)!important;
      button {
        width: 40px;
      }
    }
  }

  // Logo placement mirrors thunderstock.ai's nav logo: 128x167, flush to the
  // top edge, 35px from the left.
  .login-header {
    position: fixed;
    top: 0;
    left: 45px;
    // Sits above .auth-main, which is positioned and paints its background
    // over earlier-in-DOM siblings otherwise.
    z-index: 20;

    img {
      display: block;
      width: 128px;
      height: 167px;
    }
  }

  // Mirrors thunderstock.ai's top-right nav item ("LOGIN"): Space Grotesk 17.5px
  // / 500, white, uppercase. Inset 45px on the right to balance the logo.
  .info-link {
    position: fixed;
    top: 32px;
    right: 45px;
    z-index: 20;
    color: #ffffff;
    font-family: 'Space Grotesk', Arial, sans-serif;
    font-size: 17.5px;
    font-weight: 500;
    text-transform: uppercase;
    text-decoration: none;

    // White cube that fades in to the left of the text on hover, mirroring
    // thunderstock.ai's nav. Absolutely positioned so the text never shifts.
    &::before {
      content: '';
      position: absolute;
      right: 100%;
      top: 50%;
      margin-right: 6px;
      transform: translateY(-50%);
      width: 8px;
      height: 8px;
      background: #ffffff;
      opacity: 0;
      transition: opacity 0.2s;
    }
    &:hover::before {
      opacity: 1;
    }
  }

  // Footer clock mirrors thunderstock.ai's `footer_bottom_legal`: Space Grotesk
  // 15.5px, its own 1px white top rule inset 35px each side, 12px above the text.
  .auth-footer {
    position: absolute;
    bottom: 44px;
    left: 35px;
    right: 35px;
    padding-top: 12px;
    border-top: 1px solid #ffffff;
    color: #ffffff;
    font-family: 'Space Grotesk', Arial, sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 2;
    z-index: 10;
  }
}

</style>
